(function () {
  "use strict";

  const GLOBAL_KEY = "MarineMaxCoveoSearchHistory";
  if (window[GLOBAL_KEY]) return;

  const VERSION = 1;
  const HISTORY_STATE_KEY = "__marineMaxCoveoSearchHistory";
  const STORAGE_PREFIX = "marinemax:coveo-search-history:";
  const SNAPSHOT_TTL_MS = 30 * 60 * 1000;
  const FUTURE_TIMESTAMP_TOLERANCE_MS = 60 * 1000;
  const MAX_SNAPSHOT_CHARACTERS = 1536 * 1024;
  const MAX_STORED_SNAPSHOTS = 3;
  const SEARCH_ACTION_PREFIX = "commerce/search/";

  const engineRecords = new WeakMap();
  const restoredEngines = new WeakSet();
  const scrollRestoredEngines = new WeakSet();
  let pendingRestoreResolved = false;
  let pendingRestore = null;

  function now() {
    return Date.now();
  }

  function canonicalizeUrl(value) {
    try {
      const url = new URL(value || window.location.href, window.location.href);
      url.hash = "";
      url.searchParams.sort();
      return url.toString();
    } catch (_) {
      return "";
    }
  }

  function cloneHistoryState() {
    const current = window.history && window.history.state;
    return current && typeof current === "object" && !Array.isArray(current)
      ? Object.assign({}, current)
      : {};
  }

  function getHistoryMarker(state) {
    const source =
      state && typeof state === "object" && !Array.isArray(state)
        ? state
        : null;
    const marker = source && source[HISTORY_STATE_KEY];
    if (!marker || typeof marker !== "object") return null;
    const snapshotId = String(marker.snapshotId || "").trim();
    const rawUrl = String(marker.url || "").trim();
    const url = rawUrl ? canonicalizeUrl(rawUrl) : "";
    const savedAt = Number(marker.savedAt);
    if (
      marker.version !== VERSION ||
      !snapshotId ||
      !url ||
      !Number.isFinite(savedAt)
    ) {
      return null;
    }
    return { savedAt, snapshotId, url, version: VERSION };
  }

  function getStorageKey(snapshotId) {
    return STORAGE_PREFIX + String(snapshotId || "");
  }

  function safeRemoveStoredSnapshot(snapshotId) {
    if (!snapshotId) return;
    try {
      window.sessionStorage.removeItem(getStorageKey(snapshotId));
    } catch (_) { }
  }

  function replaceHistoryState(nextState, nextUrl) {
    try {
      window.history.replaceState(
        nextState,
        "",
        nextUrl || window.location.href,
      );
      return true;
    } catch (_) {
      return false;
    }
  }

  function clearCurrentMarker(options) {
    const opts = options || {};
    const currentState = cloneHistoryState();
    const marker = getHistoryMarker(currentState);
    delete currentState[HISTORY_STATE_KEY];
    if (!replaceHistoryState(currentState, opts.url || window.location.href)) {
      return false;
    }
    if (marker) safeRemoveStoredSnapshot(marker.snapshotId);
    pendingRestore = null;
    pendingRestoreResolved = true;
    return true;
  }

  function getNavigationType() {
    try {
      const entries = window.performance?.getEntriesByType?.("navigation");
      return String((entries && entries[0] && entries[0].type) || "");
    } catch (_) {
      return "";
    }
  }

  function getResponseFromSearchAction(action) {
    const response = action?.payload?.response;
    if (!response || typeof response !== "object" || Array.isArray(response)) {
      return null;
    }
    const products = Array.isArray(response.products)
      ? response.products
      : Array.isArray(response.results)
        ? response.results
        : null;
    const responseId = String(
      response.responseId || response.searchUid || "",
    ).trim();
    if (!responseId || !products || products.length === 0) return null;
    return { products, responseId };
  }

  function isCompletedSearchAction(action) {
    const actionType = String(action?.type || "");
    return !!(
      action &&
      typeof action === "object" &&
      !Array.isArray(action) &&
      actionType.startsWith(SEARCH_ACTION_PREFIX) &&
      actionType.endsWith("/fulfilled") &&
      action.meta &&
      action.meta.requestStatus === "fulfilled" &&
      getResponseFromSearchAction(action)
    );
  }

  function sanitizeSearchAction(action) {
    if (!isCompletedSearchAction(action)) return null;
    try {
      const clone = JSON.parse(
        JSON.stringify(action, function (key, value) {
          return /^(?:access[_-]?token|authorization)$/i.test(key)
            ? undefined
            : value;
        }),
      );
      if (clone?.payload && typeof clone.payload === "object") {
        // Coveo includes the live API access token in this request echo. The
        // reducers hydrate from the response and do not need requestExecuted.
        delete clone.payload.requestExecuted;
      }
      return isCompletedSearchAction(clone) ? clone : null;
    } catch (_) {
      return null;
    }
  }

  function getSearchProducts(searchState) {
    if (!searchState || typeof searchState !== "object") return [];
    const products =
      searchState.products ||
      searchState.results ||
      searchState.response?.products ||
      searchState.response?.results ||
      searchState.searchResponse?.products ||
      searchState.searchResponse?.results ||
      [];
    return Array.isArray(products) ? products : [];
  }

  function getCommerceResponseId(rootState) {
    const searchState = rootState && rootState.commerceSearch;
    return String(
      searchState?.responseId ||
        searchState?.searchResponseId ||
        searchState?.response?.responseId ||
        searchState?.response?.searchUid ||
        "",
    ).trim();
  }

  function getProductIdentity(product) {
    const fields = product?.additionalFields || {};
    return String(
      product?.permanentid ||
        fields.permanentid ||
        product?.productId ||
        product?.ec_product_id ||
        product?.documentUri ||
        product?.clickUri ||
        product?.url ||
        "",
    );
  }

  function stateMatchesSearchAction(rootState, action) {
    const actionResponse = getResponseFromSearchAction(action);
    const stateProducts = getSearchProducts(rootState?.commerceSearch);
    if (
      !actionResponse ||
      rootState?.commerceSearch?.isLoading === true ||
      rootState?.commerceSearch?.error ||
      getCommerceResponseId(rootState) !== actionResponse.responseId ||
      stateProducts.length !== actionResponse.products.length
    ) {
      return false;
    }
    return actionResponse.products.every(function (product, index) {
      const expected = getProductIdentity(product);
      const actual = getProductIdentity(stateProducts[index]);
      return !expected || !actual || expected === actual;
    });
  }

  function readStoredPayload(marker) {
    if (!marker) return null;
    try {
      const raw = window.sessionStorage.getItem(
        getStorageKey(marker.snapshotId),
      );
      if (!raw || raw.length > MAX_SNAPSHOT_CHARACTERS) return null;
      const payload = JSON.parse(raw);
      const savedAt = Number(payload && payload.savedAt);
      const currentTime = now();
      const searchAction = sanitizeSearchAction(payload?.searchAction);
      const payloadUrl = String(payload?.url || "").trim();
      if (
        !payload ||
        payload.version !== VERSION ||
        String(payload.snapshotId || "") !== marker.snapshotId ||
        !payloadUrl ||
        canonicalizeUrl(payloadUrl) !== marker.url ||
        !Number.isFinite(savedAt) ||
        savedAt !== marker.savedAt ||
        savedAt > currentTime + FUTURE_TIMESTAMP_TOLERANCE_MS ||
        currentTime - savedAt > SNAPSHOT_TTL_MS ||
        !String(payload.contextFingerprint || "").trim() ||
        !String(payload.coveoVersion || "").trim() ||
        !searchAction
      ) {
        return null;
      }
      return {
        contextFingerprint: String(payload.contextFingerprint),
        coveoVersion: String(payload.coveoVersion),
        savedAt,
        scrollX: Math.max(0, Number(payload.scrollX) || 0),
        scrollY: Math.max(0, Number(payload.scrollY) || 0),
        searchAction,
        snapshotId: marker.snapshotId,
        url: marker.url,
        version: VERSION,
      };
    } catch (_) {
      return null;
    }
  }

  function resolvePendingRestore() {
    if (pendingRestoreResolved) return pendingRestore;
    pendingRestoreResolved = true;

    const marker = getHistoryMarker(window.history && window.history.state);
    if (!marker) return null;

    const currentUrl = canonicalizeUrl(window.location.href);
    if (
      getNavigationType() !== "back_forward" ||
      !currentUrl ||
      marker.url !== currentUrl
    ) {
      clearCurrentMarker();
      return null;
    }

    const payload = readStoredPayload(marker);
    if (!payload) {
      clearCurrentMarker();
      return null;
    }
    pendingRestore = payload;
    return pendingRestore;
  }

  function createSnapshotId() {
    try {
      if (typeof window.crypto?.randomUUID === "function") {
        return window.crypto.randomUUID();
      }
    } catch (_) { }
    return `${now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }

  function isSameOriginProductUrl(href) {
    try {
      const destination = new URL(href, window.location.href);
      return (
        destination.origin === window.location.origin &&
        /(?:^|\/)products\//i.test(destination.pathname)
      );
    } catch (_) {
      return false;
    }
  }

  function pruneStoredSnapshots(keepSnapshotId) {
    try {
      const records = [];
      for (let index = 0; index < window.sessionStorage.length; index += 1) {
        const key = window.sessionStorage.key(index);
        if (!key || !key.startsWith(STORAGE_PREFIX)) continue;
        const snapshotId = key.slice(STORAGE_PREFIX.length);
        let savedAt = 0;
        try {
          savedAt =
            Number(JSON.parse(window.sessionStorage.getItem(key)).savedAt) || 0;
        } catch (_) { }
        records.push({ key, savedAt, snapshotId });
      }
      records
        .sort(function (left, right) {
          return right.savedAt - left.savedAt;
        })
        .forEach(function (record, index) {
          const isExpired = now() - record.savedAt > SNAPSHOT_TTL_MS;
          const isOverflow =
            index >= MAX_STORED_SNAPSHOTS &&
            record.snapshotId !== keepSnapshotId;
          if (!record.savedAt || isExpired || isOverflow) {
            window.sessionStorage.removeItem(record.key);
          }
        });
    } catch (_) { }
  }

  function hashFingerprint(value) {
    const input = String(value || "");
    let first = 2166136261;
    let second = 2246822519;
    for (let index = 0; index < input.length; index += 1) {
      const code = input.charCodeAt(index);
      first = Math.imul(first ^ code, 16777619);
      second = Math.imul(second ^ code, 3266489917);
    }
    return `${input.length}:${(first >>> 0).toString(36)}:${(
      second >>> 0
    ).toString(36)}`;
  }

  function buildEngineContextFingerprint(engineOptions, metadata) {
    try {
      const configuration = engineOptions?.configuration || {};
      const context = configuration.context || {};
      const locationFingerprint =
        typeof metadata?.getLocationFingerprint === "function"
          ? metadata.getLocationFingerprint()
          : metadata?.locationFingerprint;
      const cart = Array.isArray(context.cart)
        ? context.cart.map(function (item) {
            return {
              productId: String(item?.productId || ""),
              quantity: Number(item?.quantity) || 0,
            };
          })
        : [];
      return hashFingerprint(JSON.stringify({
        cart,
        country: String(context.country || ""),
        currency: String(context.currency || ""),
        customerId: String(metadata?.customerId || "anonymous"),
        language: String(context.language || ""),
        location: String(locationFingerprint || "none"),
        organizationId: String(configuration.organizationId || ""),
        trackingId: String(configuration.analytics?.trackingId || ""),
        viewUrl: canonicalizeUrl(context.view?.url || ""),
      }));
    } catch (_) {
      return "";
    }
  }

  function createEngineStateBridge() {
    let readState = null;
    let completedSearchAction = null;
    const middleware = function (api) {
      readState =
        api && typeof api.getState === "function"
          ? api.getState.bind(api)
          : null;
      return function (next) {
        return function (action) {
          const result = next(action);
          if (
            isCompletedSearchAction(action) &&
            stateMatchesSearchAction(readState ? readState() : null, action)
          ) {
            completedSearchAction = action;
          }
          return result;
        };
      };
    };
    return Object.freeze({
      getCompletedSearchAction: function () {
        return completedSearchAction;
      },
      getState: function () {
        return readState ? readState() : null;
      },
      middleware,
    });
  }

  function makeRestoreResult(attempted, restored, requiresFreshSearch) {
    return Object.freeze({
      attempted: attempted === true,
      requiresFreshSearch: requiresFreshSearch === true,
      restored: restored === true,
    });
  }

  function registerEngine(engine, bridge, options) {
    if (!engine || typeof engine !== "object" || !bridge) {
      return false;
    }
    const opts = options || {};
    const coveoVersion = String(opts.metadata?.coveoVersion || "").trim();
    engineRecords.set(engine, {
      bridge,
      coveoVersion,
      engineOptions: opts.engineOptions,
      metadata: opts.metadata,
    });
    return true;
  }

  function restoreEngine(engine, options) {
    const opts = options || {};
    if (opts.allow === false) {
      clearCurrentMarker();
      return makeRestoreResult(false, false, false);
    }
    const engineRecord = engineRecords.get(engine);
    if (!engineRecord) return makeRestoreResult(false, false, false);

    const restore = resolvePendingRestore();
    if (!restore) return makeRestoreResult(false, false, false);
    const coveoVersion = String(engineRecord.coveoVersion || "").trim();
    const contextFingerprint = buildEngineContextFingerprint(
      engineRecord.engineOptions,
      engineRecord.metadata,
    );
    if (
      !coveoVersion ||
      restore.coveoVersion !== coveoVersion ||
      !contextFingerprint ||
      restore.contextFingerprint !== contextFingerprint
    ) {
      clearCurrentMarker();
      return makeRestoreResult(false, false, false);
    }

    try {
      engine.dispatch(restore.searchAction);
    } catch (_) {
      clearCurrentMarker();
      return makeRestoreResult(true, false, true);
    }

    let currentState = null;
    try {
      currentState = engineRecord.bridge.getState();
    } catch (_) { }
    if (!stateMatchesSearchAction(currentState, restore.searchAction)) {
      clearCurrentMarker();
      return makeRestoreResult(true, false, true);
    }

    restoredEngines.add(engine);
    return makeRestoreResult(true, true, false);
  }

  function isEngineRestored(engine) {
    return !!engine && restoredEngines.has(engine);
  }

  function capture(options) {
    const opts = options || {};
    const engine = opts.engine;
    if (!engine || !isSameOriginProductUrl(opts.href)) return false;
    const failCapture = function () {
      clearCurrentMarker();
      return false;
    };
    const engineRecord = engineRecords.get(engine);
    const bridge = engineRecord && engineRecord.bridge;
    const contextFingerprint = engineRecord
      ? buildEngineContextFingerprint(
          engineRecord.engineOptions,
          engineRecord.metadata,
        )
      : "";
    if (
      !bridge ||
      typeof bridge.getCompletedSearchAction !== "function" ||
      typeof bridge.getState !== "function" ||
      !String(contextFingerprint || "").trim() ||
      !String(engineRecord.coveoVersion || "").trim()
    ) {
      return failCapture();
    }

    const searchAction = sanitizeSearchAction(
      bridge.getCompletedSearchAction(),
    );
    if (!searchAction) return failCapture();
    let currentState = null;
    try {
      currentState = bridge.getState();
    } catch (_) {
      return failCapture();
    }
    if (!stateMatchesSearchAction(currentState, searchAction)) {
      return failCapture();
    }

    const currentUrl = canonicalizeUrl(window.location.href);
    if (!currentUrl) return failCapture();
    const snapshotId = createSnapshotId();
    const savedAt = now();
    const payload = {
      contextFingerprint,
      coveoVersion: engineRecord.coveoVersion,
      savedAt,
      scrollX: Math.max(0, Number(window.scrollX) || 0),
      scrollY: Math.max(0, Number(window.scrollY) || 0),
      searchAction,
      snapshotId,
      url: currentUrl,
      version: VERSION,
    };

    let serialized = "";
    try {
      serialized = JSON.stringify(payload);
      if (!serialized || serialized.length > MAX_SNAPSHOT_CHARACTERS) {
        return failCapture();
      }
      window.sessionStorage.setItem(getStorageKey(snapshotId), serialized);
    } catch (_) {
      return failCapture();
    }

    const previousMarker = getHistoryMarker(
      window.history && window.history.state,
    );
    const nextState = cloneHistoryState();
    nextState[HISTORY_STATE_KEY] = {
      savedAt,
      snapshotId,
      url: currentUrl,
      version: VERSION,
    };
    if (!replaceHistoryState(nextState, window.location.href)) {
      safeRemoveStoredSnapshot(snapshotId);
      return failCapture();
    }
    if (previousMarker && previousMarker.snapshotId !== snapshotId) {
      safeRemoveStoredSnapshot(previousMarker.snapshotId);
    }
    pruneStoredSnapshots(snapshotId);
    return true;
  }

  function replaceUrl(nextHref) {
    const nextUrl = canonicalizeUrl(nextHref) || String(nextHref || "");
    const nextState = cloneHistoryState();
    const marker = getHistoryMarker(nextState);
    const shouldInvalidate = !!(marker && nextUrl && marker.url !== nextUrl);
    if (shouldInvalidate) delete nextState[HISTORY_STATE_KEY];
    if (!replaceHistoryState(nextState, nextHref)) return false;
    if (shouldInvalidate) {
      safeRemoveStoredSnapshot(marker.snapshotId);
      pendingRestore = null;
      pendingRestoreResolved = true;
    }
    return true;
  }

  function restoreScroll(engine) {
    if (!isEngineRestored(engine) || !pendingRestore) return false;
    if (scrollRestoredEngines.has(engine)) return false;
    if (typeof window.scrollTo !== "function") return false;
    const engineRecord = engineRecords.get(engine);
    let currentState = null;
    try {
      currentState = engineRecord?.bridge?.getState?.();
    } catch (_) { }
    if (
      !engineRecord ||
      !stateMatchesSearchAction(currentState, pendingRestore.searchAction)
    ) {
      return false;
    }
    scrollRestoredEngines.add(engine);

    const targetX = pendingRestore.scrollX;
    const targetY = pendingRestore.scrollY;
    const scheduleFrame =
      typeof window.requestAnimationFrame === "function"
        ? window.requestAnimationFrame.bind(window)
        : function (callback) {
            return window.setTimeout(callback, 0);
          };
    let attempts = 0;
    const apply = function () {
      attempts += 1;
      try {
        window.scrollTo(targetX, targetY);
      } catch (_) {
        return;
      }
      const currentY = Number(window.scrollY) || 0;
      if (Math.abs(currentY - targetY) > 2 && attempts < 12) {
        window.setTimeout(apply, 50);
      }
    };
    scheduleFrame(function () {
      scheduleFrame(apply);
    });
    return true;
  }

  const runtime = Object.freeze({
    capture,
    constants: Object.freeze({
      historyStateKey: HISTORY_STATE_KEY,
      snapshotTtlMs: SNAPSHOT_TTL_MS,
      storagePrefix: STORAGE_PREFIX,
      version: VERSION,
    }),
    createEngineStateBridge,
    invalidateCurrentEntry: clearCurrentMarker,
    isEngineRestored,
    registerEngine,
    replaceUrl,
    restoreEngine,
    restoreScroll,
  });

  Object.defineProperty(window, GLOBAL_KEY, {
    configurable: false,
    enumerable: false,
    value: runtime,
    writable: false,
  });
})();
